import React, { useEffect, useState } from 'react'
import { Box, Alert, CircularProgress, Tabs, Tab } from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { apiService } from '../services/api'
import SendReportModal from '../components/dashboard/SendReportModal'
import {
  ReportPage, HeaderButton, FilterBar, KpiStrip, Section, ReportTable, Icon, fmt, theme,
  useDistinctFilters, buildOplFilterFields,
} from '../components/report-shell'

const monthStartIso = () => {
  const d = new Date(); d.setDate(1)
  return d.toISOString().slice(0, 10)
}

const nextMonthIso = (isoDate) => {
  const [year, month] = isoDate.split('-').map(Number)
  const d = new Date(Date.UTC(year, month, 1))
  return d.toISOString().slice(0, 10)
}

const PRIORITY_LABEL = {
  P1: '🔴 P1 (półkowy bez sprzedaży)',
  P2: '🟡 P2 (aktywny w ostatnich 3 m-cach)',
  P3: '⚫ P3 (długo nieaktywny)',
}

const PRIORITY_KIND = { P1: 'bad', P2: 'warn', P3: 'neutral' }

const OrangeLostOpportunitiesPage = () => {
  const [monthStart, setMonthStart] = useState(monthStartIso())
  const [filters, setFilters] = useState({})
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(0)
  const [sendModal, setSendModal] = useState({ open: false, scope: null, value: null })

  const distinct = useDistinctFilters('opl')
  const allFilters = () => ({ month_start: monthStart, ...filters })

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      const resp = await apiService.getOrangeLostOpportunities(allFilters())
      setData(resp.data)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  const exportExcel = async () => {
    setExporting(true)
    try {
      const resp = await apiService.exportOrangeLostOpportunities(allFilters())
      const url = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Utracone_szanse_OPL_${monthStart.slice(0, 7)}.xlsx`)
      document.body.appendChild(link); link.click(); link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setExporting(false) }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData() }, [monthStart, JSON.stringify(filters)])

  const T = data?.totals || {}

  const rollupCols = (labelField, label, scope) => [
    {
      field: '_send', header: '', width: '40px', sortable: false,
      render: (_v, row) => {
        const value = row[scope] || row.region || row.dystrykt || row.agent || row.kam
        if (!value || value === '(brak)') return null
        return (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); setSendModal({ open: true, scope, value }) }}
            title={`Wyślij raport R3 (${scope})`}
            style={{
              display: 'inline-flex', cursor: 'pointer',
              color: theme.textSub, padding: 4, borderRadius: 5,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = theme.accent }}
            onMouseLeave={(e) => { e.currentTarget.style.color = theme.textSub }}
          >
            <EmailIcon fontSize="small" sx={{ width: 14, height: 14 }} />
          </span>
        )
      },
    },
    { field: labelField, header: label, width: '1.7fr', minWidth: 200 },
    { field: 'salon_count', header: 'Bez sprzedaży', width: '130px', numeric: true },
  ]

  const cols = [
    {
      field: 'priority', header: 'Priorytet', width: '110px',
      render: (v) => v ? (
        <span title={PRIORITY_LABEL[v]}
          style={{
            display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
            fontSize: 11, fontWeight: 600,
            background:
              PRIORITY_KIND[v] === 'bad' ? theme.badSub :
              PRIORITY_KIND[v] === 'warn' ? theme.warnSub : theme.bgSub,
            color:
              PRIORITY_KIND[v] === 'bad' ? theme.bad :
              PRIORITY_KIND[v] === 'warn' ? theme.warn : theme.textSub,
            border: `1px solid ${
              PRIORITY_KIND[v] === 'bad' ? `${theme.bad}33` :
              PRIORITY_KIND[v] === 'warn' ? `${theme.warn}33` : theme.border}`,
          }}>{v}</span>
      ) : '',
    },
    { field: 'is_polkowy', header: 'PB', width: '60px', sortable: false,
      render: (v) => v ? '🛒' : '' },
    { field: 'kod_bscs', header: 'BSCS', width: '110px' },
    { field: 'city', header: 'Miasto', width: '140px' },
    { field: 'kam', header: 'KAM', width: '110px' },
    { field: 'agent', header: 'Agent', width: '1.4fr', minWidth: 200 },
    { field: 'rksa', header: 'RKSA', width: '160px' },
    { field: 'rksa_email', header: 'Email RKSA', width: '200px', email: true },
    { field: 'xdm', header: 'XDM', width: '150px' },
    { field: 'xdm_phone', header: 'Tel XDM', width: '130px', phone: true },
    { field: 'nazwa_regionu', header: 'Region', width: '120px' },
    { field: 'last_sale', header: 'Ost. sprzedaż', width: '130px' },
    { field: 'days_since_last_sale', header: 'Dni od', width: '90px', numeric: true },
  ]

  // Soft client-side filters (priority + polkoweOnly).
  const filteredSalons = (data?.salons || []).filter(s => {
    if (filters.priority && s.priority !== filters.priority) return false
    if (filters.polkoweOnly && !s.is_polkowy) return false
    return true
  })

  const filterFields = [
    ...buildOplFilterFields(distinct, filters, setFilters),
    {
      type: 'select', key: 'priority', label: 'Priorytet',
      value: filters.priority ?? null,
      options: [
        { value: null, label: 'Wszystkie' },
        { value: 'P1', label: 'P1 — półkowe' },
        { value: 'P2', label: 'P2 — aktywne historycznie' },
        { value: 'P3', label: 'P3 — długo nieaktywne' },
      ],
      onChange: (v) => setFilters(s => ({ ...s, priority: v || undefined })),
    },
    {
      type: 'toggle', key: 'polkoweOnly', label: 'Tylko salony półkowe',
      value: !!filters.polkoweOnly,
      onChange: (v) => setFilters(s => ({ ...s, polkoweOnly: v || undefined })),
    },
  ]

  const filterBar = (
    <FilterBar
      periods={[
        {
          type: 'date', key: 'monthStart', label: 'Miesiąc od',
          value: monthStart, onChange: (v) => setMonthStart(v || monthStartIso()),
        },
      ]}
      periodRight={data?.evaluated_at ? `Stan na ${data.evaluated_at}` : null}
      filters={filterFields}
      onReset={() => setFilters({})}
    />
  )

  const kpiTiles = data ? [
    { key: 'lost', label: 'Salony bez sprzedaży', value: fmt.int(T.lost_count || 0), accent: '#dc2626' },
    { key: 'p1',   label: 'P1 — półkowe',          value: fmt.int(T.p1_polkowe || 0), accent: '#dc2626' },
    { key: 'p2',   label: 'P2 — aktywne historycznie', value: fmt.int(T.p2_aktywne_historycznie || 0), accent: '#d97706' },
    { key: 'p3',   label: 'P3 — długo nieaktywne', value: fmt.int(T.p3_nieaktywne || 0), accent: '#8a827a' },
  ] : []

  const tabConfigs = [
    { label: `Per region (${data?.regions?.length || 0})`, rows: data?.regions || [], cols: rollupCols('region', 'Region', 'region'), keyField: 'region' },
    { label: `Per dystrykt (${data?.districts?.length || 0})`, rows: data?.districts || [], cols: rollupCols('dystrykt', 'Dystrykt', 'district'), keyField: 'dystrykt' },
    { label: `Per agent (${data?.agents?.length || 0})`, rows: data?.agents || [], cols: rollupCols('agent', 'Agent', 'agent'), keyField: 'agent' },
    { label: `Per KAM (${data?.kams?.length || 0})`, rows: data?.kams || [], cols: rollupCols('kam', 'KAM', 'kam'), keyField: 'kam' },
    { label: `Salony (${filteredSalons.length})`, rows: filteredSalons, cols, keyField: 'plant_code' },
  ]
  const active = tabConfigs[tab]

  return (
    <ReportPage
      actions={
        <>
          <HeaderButton onClick={fetchData} disabled={loading}>
            <Icon.refresh /> {loading ? 'Ładowanie…' : 'Odśwież'}
          </HeaderButton>
          <HeaderButton primary onClick={exportExcel} disabled={exporting || !data}>
            <Icon.download /> {exporting ? 'Eksport…' : 'Eksport Excel'}
          </HeaderButton>
        </>
      }
      filterBar={filterBar}
      kpiStrip={kpiTiles.length > 0 ? <KpiStrip tiles={kpiTiles} /> : null}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {data && !loading && (
        <Section
          title="Lista utraconych szans"
          right={
            <Box sx={{ borderBottom: 0 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
                {tabConfigs.map((t, i) => <Tab key={i} label={t.label} />)}
              </Tabs>
            </Box>
          }
        >
          <ReportTable
            rows={active.rows.map((r, i) => ({ id: r[active.keyField] || i, ...r }))}
            columns={active.cols}
            initialSort={tab === 4 ? { field: 'priority', dir: 'asc' } : { field: 'salon_count', dir: 'desc' }}
            pageSize={tab === 4 ? 50 : 25}
            pageSizeOptions={[25, 50, 100, 500]}
            density="compact"
          />
        </Section>
      )}

      <SendReportModal
        open={sendModal.open}
        onClose={() => setSendModal({ open: false, scope: null, value: null })}
        operator="orange"
        scope={sendModal.scope}
        scopeValue={sendModal.value}
        reportType="orange_lost_opportunities"
        periodStart={monthStart}
        periodEnd={nextMonthIso(monthStart)}
      />
    </ReportPage>
  )
}

export default OrangeLostOpportunitiesPage
